import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getApiBaseUrl } from "../../config";

const baseQuery = fetchBaseQuery({
  baseUrl: getApiBaseUrl(),
  credentials: "include",
  prepareHeaders: (headers, { getState }) => {
    const user = getState().user?.currentUser;
    if (user?.id) {
      headers.set("userid", user.id);
    }
    return headers;
  },
});

export const productCatalogApi = createApi({
  reducerPath: "productCatalogApi",
  baseQuery,
  tagTypes: ["ProductCatalog", "ProductCatalogItems"],
  endpoints: (builder) => ({
    uploadProductCatalog: builder.mutation({
      async queryFn({ companyProfileId, catalogId, formData }, queryApi, extraOptions, baseQuery) {
        try {
          if (companyProfileId) {
            formData.append("company_profile_id", companyProfileId);
          }
          if (catalogId) {
            formData.append("catalog_id", catalogId);
          }
          const result = await baseQuery({
            url: "product-catalog/upload",
            method: "POST",
            body: formData,
          });
          if (result.error) return { error: result.error };
          const data = result.data || {};
          return { data: { ...data, success: true } };
        } catch (error) {
          console.error("Error uploading product catalog:", error);
          return { error: "Failed to upload product catalog" };
        }
      },
      invalidatesTags: ["ProductCatalog", "ProductCatalogItems"],
    }),
    createManualProductCatalog: builder.mutation({
      async queryFn({ companyProfileId, items }, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: "product-catalog",
            method: "POST",
            body: { company_profile_id: companyProfileId, items },
          });
          if (result.error) return { error: result.error };
          const data = result.data || {};
          return { data: { ...data, success: true } };
        } catch (error) {
          console.error("Error creating manual product catalog:", error);
          return { error: "Failed to create product catalog" };
        }
      },
      invalidatesTags: ["ProductCatalog", "ProductCatalogItems"],
    }),
    getLatestProductCatalog: builder.query({
      async queryFn(companyProfileId, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: `product-catalog/company/${companyProfileId}/latest`,
          });
          if (result.error) return { error: result.error };
          const data = result.data || {};
          return { data: data.data || null };
        } catch (error) {
          console.error("Error fetching latest product catalog:", error);
          return { data: null };
        }
      },
      providesTags: (result, error, id) => [{ type: "ProductCatalog", id }],
    }),
    getProductCatalogStatus: builder.query({
      async queryFn(companyProfileId, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: `product-catalog/company/${companyProfileId}/status`,
          });
          if (result.error) return { error: result.error };
          const data = result.data || {};
          return { data: data.data || null };
        } catch (error) {
          console.error("Error fetching product catalog status:", error);
          return { data: null };
        }
      },
      providesTags: (result, error, id) => [{ type: "ProductCatalog", id }],
    }),
    getProductCatalogItems: builder.query({
      async queryFn(id, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: `product-catalog/${id}/items`,
          });
          if (result.error) return { error: result.error };
          const data = result.data || {};
          return { data: data.data || [] };
        } catch (error) {
          console.error("Error fetching catalog items:", error);
          return { data: [] };
        }
      },
      providesTags: (result, error, id) => [{ type: "ProductCatalogItems", id }],
    }),
    getProductCatalogPricing: builder.query({
      async queryFn(id, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: `product-catalog/${id}/pricing`,
          });
          if (result.error) return { error: result.error };
          const data = result.data || {};
          return { data: data.data || null };
        } catch (error) {
          console.error("Error fetching pricing:", error);
          return { data: null };
        }
      },
      providesTags: (result, error, id) => [{ type: "ProductCatalog", id }],
    }),
    updateCatalogItem: builder.mutation({
      async queryFn({ id, itemId, ...patch }, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: `product-catalog/${id}/items/${itemId}`,
            method: "PUT",
            body: patch,
          });
          if (result.error) return { error: result.error };
          const data = result.data || {};
          return { data: { ...data, success: true } };
        } catch (error) {
          console.error("Error updating catalog item:", error);
          return { error: "Failed to update catalog item" };
        }
      },
      invalidatesTags: (result, error, { id }) => [{ type: "ProductCatalogItems", id }, "ProductCatalog"],
    }),
    saveCatalogItemsBulk: builder.mutation({
      async queryFn({ id, catalogId, items }, queryApi, extraOptions, baseQuery) {
        try {
          const resolvedId = id || catalogId;
          const result = await baseQuery({
            url: `product-catalog/${resolvedId}/items/bulk`,
            method: "POST",
            body: { items },
          });
          if (result.error) return { error: result.error };
          const data = result.data || {};
          return { data: { ...data, success: true } };
        } catch (error) {
          console.error("Error saving catalog items:", error);
          return { error: "Failed to save catalog items" };
        }
      },
      invalidatesTags: (result, error, { id, catalogId }) => [
        { type: "ProductCatalogItems", id: id || catalogId },
        "ProductCatalog",
      ],
    }),
    updateProductCatalog: builder.mutation({
      async queryFn({ id, ...patch }, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: `product-catalog/${id}`,
            method: "PATCH",
            body: patch,
          });
          if (result.error) return { error: result.error };
          const data = result.data || {};
          return { data: { ...data, success: true } };
        } catch (error) {
          console.error("Error updating product catalog:", error);
          return { error: "Failed to update product catalog" };
        }
      },
      invalidatesTags: (result, error, { id }) => [{ type: "ProductCatalog", id }],
    }),
    addCatalogItem: builder.mutation({
      async queryFn({ catalogId, item }, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: `product-catalog/${catalogId}/items/bulk`,
            method: "POST",
            body: { items: [item] },
          });
          if (result.error) return { error: result.error };
          const data = result.data || {};
          return { data: { ...data, success: true } };
        } catch (error) {
          console.error("Error adding catalog item:", error);
          return { error: "Failed to add catalog item" };
        }
      },
      invalidatesTags: (result, error, { catalogId }) => [
        { type: "ProductCatalogItems", id: catalogId },
        "ProductCatalog",
      ],
    }),
    deleteProductCatalog: builder.mutation({
      async queryFn(id, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: `product-catalog/${id}`,
            method: "DELETE",
          });
          if (result.error) return { error: result.error };
          const data = result.data || {};
          return { data: { ...data, success: true } };
        } catch (error) {
          console.error("Error deleting product catalog:", error);
          return { error: "Failed to delete product catalog" };
        }
      },
      invalidatesTags: ["ProductCatalog", "ProductCatalogItems"],
    }),
    deleteCatalogItem: builder.mutation({
      async queryFn({ catalogId, itemId }, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: `product-catalog/${catalogId}/items/${itemId}`,
            method: "DELETE",
          });
          if (result.error) return { error: result.error };
          const data = result.data || {};
          return { data: { ...data, success: true } };
        } catch (error) {
          console.error("Error deleting catalog item:", error);
          return { error: "Failed to delete catalog item" };
        }
      },
      invalidatesTags: (result, error, { catalogId }) => [
        { type: "ProductCatalogItems", id: catalogId },
        "ProductCatalog",
      ],
    }),
  }),
});

export const {
  useUploadProductCatalogMutation,
  useCreateManualProductCatalogMutation,
  useGetLatestProductCatalogQuery,
  useGetProductCatalogStatusQuery,
  useGetProductCatalogItemsQuery,
  useGetProductCatalogPricingQuery,
  useUpdateCatalogItemMutation,
  useSaveCatalogItemsBulkMutation,
  useUpdateProductCatalogMutation,
  useAddCatalogItemMutation,
  useDeleteProductCatalogMutation,
  useDeleteCatalogItemMutation,
} = productCatalogApi;
